// PaymentNewDialog — ручное создание платежа (V12 U-35, волна R66; V14 W7 §3.8 — «Оплатить счёт»).
// POST /api/payments {amount, currency, paymentDate?, fiscalDocNumber?, entityType+entityId, invoiceId?}
// → backend ставит source=MANUAL, status=PENDING, currency=RUB (54-ФЗ).
// Используется: список платежей (FAB «Создать платёж»), блок сущности (preset привязки),
// после 422 загрузки чека («Создать вручную» с preset), счёт (presetInvoice — «Оплатить счёт»:
// поле «Счёт №INV-…», привязка DEAL+dealId фиксируется, POST идёт с invoiceId — §3.2).
// Автокомплит счетов: DRAFT не показываем (нельзя оплатить — R4), VOID — disabled (серый);
// выбор счёта → auto-lock привязки DEAL+dealId счёта (§3.8).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import { paymentsApi, type Payment } from '@/api/payments';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { DEAL_CURRENCIES } from '@/api/deals';
import { EntityBindingSelect, type PaymentBinding } from './EntityBindingSelect';
import { DateTimeField } from '@/components/forms';
import { parseAmount } from '@/lib/money';
import { formatInvoiceNumber } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Валюты платежа — единый FE-канон DEAL_CURRENCIES (V14 W8 §3.8.1: [RUB,USD,EUR,GBP]; 54-ФЗ: RUB по умолчанию). */
const CURRENCIES = DEAL_CURRENCIES;

/** Задержка staleTime для справочника счетов (меняются редко, размер ограничен backend-каноном). */
const INVOICE_OPTIONS_STALE_MS = 5 * 60 * 1000;

interface PaymentNewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Предзаполненная привязка (блок карточки / 422-чек → «Создать вручную»). */
  preset?: PaymentBinding | null;
  /** Счёт к оплате («Оплатить счёт» из InvoiceBlock/InvoicesListPage, V14 W7 §3.8): диалог
   *  показывает поле «Счёт №INV-…», фиксирует привязку DEAL+dealId и POST'ит с invoiceId. */
  presetInvoice?: Invoice | null;
  /** Колбэк успешного создания (например, навигация/бейдж). */
  onCreated?: (payment: Payment) => void;
}

/** Диалог ручного создания платежа (source=MANUAL; обязательны сумма и привязка). */
export function PaymentNewDialog({ open, onClose, preset = null, presetInvoice = null, onCreated }: PaymentNewDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<string>('RUB');
  const [paymentDate, setPaymentDate] = useState('');
  const [fiscalDocNumber, setFiscalDocNumber] = useState('');
  const [binding, setBinding] = useState<PaymentBinding | null>(preset);
  const [invoice, setInvoice] = useState<Invoice | null>(presetInvoice);

  // Сброс формы под диалог: preset (из карточки/422) и presetInvoice (из счёта) при каждом
  // открытии (динамический preset). Счёт → auto-lock привязки DEAL+dealId счёта (§3.8).
  useEffect(() => {
    if (!open) return;
    setBinding(presetInvoice ? { entityType: 'DEAL', entityId: presetInvoice.dealId } : preset);
    setInvoice(presetInvoice);
    setCurrency(presetInvoice ? presetInvoice.currency : 'RUB');
    setAmount('');
    setPaymentDate('');
    setFiscalDocNumber('');
  }, [open, preset, presetInvoice]);

  /** Справочник счетов для автокомплита (DRAFT не показываем; VOID — disabled в getOptionDisabled). */
  const invoicesQuery = useQuery({
    queryKey: ['invoices', 'options'],
    queryFn: () => invoicesApi.list({ page: 0, size: 100 }),
    enabled: open && !invoice,
    staleTime: INVOICE_OPTIONS_STALE_MS,
    retry: false,
  });
  const invoiceOptions = (invoicesQuery.data?.content ?? []).filter((i) => i.lifecycle !== 'DRAFT');

  /** Выбор счёта → auto-lock привязки DEAL+dealId счёта (§3.8) + валюта счёта (R4: 400 защиты). */
  const handleInvoiceSelect = (next: Invoice | null): void => {
    setInvoice(next);
    if (next) {
      setBinding({ entityType: 'DEAL', entityId: next.dealId });
      setCurrency(next.currency);
    }
  };

  const parsedAmount = parseAmount(amount);
  const canSubmit = parsedAmount !== null && parsedAmount > 0 && Boolean(binding?.entityId);

  const createMutation = useMutation({
    mutationFn: () => {
      if (!binding || parsedAmount === null) throw new Error('Привязка и сумма обязательны');
      return paymentsApi.create({
        amount: parsedAmount,
        currency,
        ...(paymentDate ? { paymentDate } : {}),
        ...(fiscalDocNumber.trim() ? { fiscalDocNumber: fiscalDocNumber.trim() } : {}),
        entityType: binding.entityType,
        entityId: binding.entityId,
        ...(invoice ? { invoiceId: invoice.id } : {}),
      });
    },
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      onCreated?.(payment);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{presetInvoice ? t('payments.payForInvoice') : t('payments.create.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {createMutation.isError ? (
            <Alert severity="error">{extractApiErrorMessage(createMutation.error, t('payments.create.error'))}</Alert>
          ) : null}
          {invoice ? (
            // «Оплатить счёт»: счёт зафиксирован (№ INV-YYYY-NNNN — OQ-1), новый выбрать нельзя
            // (auto-lock); повторный выбор — снятием привязки и пересозданием платежа (OQ-8).
            <TextField
              disabled
              fullWidth
              label={t('payments.invoice')}
              value={formatInvoiceNumber(invoice.invoiceNumber, invoice.invoiceDate)}
              inputProps={{ 'aria-label': t('payments.invoice') }}
            />
          ) : (
            <Autocomplete
              data-testid="payment-invoice-autocomplete"
              options={invoiceOptions}
              loading={invoicesQuery.isLoading}
              getOptionLabel={(option: Invoice) => formatInvoiceNumber(option.invoiceNumber, option.invoiceDate)}
              getOptionDisabled={(option: Invoice) => option.lifecycle === 'VOID'}
              isOptionEqualToValue={(option, current) => option.id === current.id}
              onChange={(_event, option) => handleInvoiceSelect(option)}
              renderInput={(params) => (
                <TextField
                  {...(params as unknown as TextFieldProps)}
                  fullWidth
                  label={`${t('payments.invoiceNumber')} (${t('payments.invoice')})`}
                  inputProps={{ ...params.inputProps, 'aria-label': t('payments.invoice') }}
                />
              )}
            />
          )}
          <TextField
            label={t('payments.amount')}
            required
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ 'aria-label': t('payments.amount') }}
          />
          <TextField
            select
            label={t('payments.currency')}
            fullWidth
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            inputProps={{ 'aria-label': t('payments.currency') }}
          >
            {CURRENCIES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          {/* V15-a: MUI X DateTimeField вместо <input type="datetime-local"> (UA-календарь
              перекрывал форму); значение — ISO Instant (BE TIMESTAMPTZ); пусто → BE-дефолт now(). */}
          <DateTimeField
            label={t('payments.paymentDate')}
            value={paymentDate || null}
            onChange={(iso) => setPaymentDate(iso ?? '')}
            clearable
            aria-label={t('payments.paymentDate')}
          />
          <TextField
            label={t('payments.fiscalDocNumber')}
            fullWidth
            value={fiscalDocNumber}
            onChange={(e) => setFiscalDocNumber(e.target.value)}
            inputProps={{ 'aria-label': t('payments.fiscalDocNumber') }}
          />
          <EntityBindingSelect value={binding} onChange={setBinding} locked={Boolean(preset) || Boolean(invoice)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending || !canSubmit}
        >
          {t('payments.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
